<template>
  <div class="video-player">
    <div class="videowrapper">
      <video v-lazy-load 
        ref="videoElement"
        class="video"
        :autoplay="autoplay"
        :loop="loop"
        :muted="muted"
        :data-poster="posterUrl || undefined"
        playsinline
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Hls from 'hls.js';
import { onBeforeUnmount, onMounted, ref } from 'vue';

type UploadVideoField = {
  __typename?: 'UploadVideoField' | string | null;
  muxPlaybackId?: string | null;
  streamingUrl?: string | null;
  mp4Med?: string | null;
};

const props = withDefaults(
  defineProps<{
    data: UploadVideoField;
    posterUrl?: string;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
  }>(),
  {
    posterUrl: '',
    autoplay: true,
    loop: true,
    muted: true
  }
);

const autoplay = props.autoplay;
const loop = props.loop;
const muted = props.muted;
const posterUrl = props.posterUrl;

const videoElement = ref<HTMLVideoElement | null>(null);
let hls: Hls | null = null;

const tryAutoplay = async () => {
  if (!autoplay || !videoElement.value) {
    return;
  }

  try {
    await videoElement.value.play();
  } catch {
    // Ignore autoplay failures caused by browser/user settings.
  }
};

onMounted(() => {
  const streamUrl = props.data.streamingUrl;
  const mp4Url = props.data.mp4Med;
  const video = videoElement.value;

  if (!video) {
    return;
  }

  if (!streamUrl && mp4Url) {
    video.src = mp4Url;
    video.addEventListener('loadedmetadata', tryAutoplay, { once: true });
    return;
  }

  if (!streamUrl) {
    return;
  }

  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    video.src = streamUrl;
    video.addEventListener(
      'loadedmetadata',
      () => {
        tryAutoplay();
      },
      { once: true }
    );
    return;
  }

  if (Hls.isSupported()) {
    hls = new Hls();
    hls.loadSource(streamUrl);
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      tryAutoplay();
    });
  }
});

onBeforeUnmount(() => {
  hls?.destroy();
  hls = null;
});
</script>

<style scoped lang="scss">
.video-player,
.videowrapper,
.video {
  width: 100%;
  height: 100%;
}

.video {
  display: block;
  object-fit: cover;
}
</style>
